import english from "./locales/en.json";
import turkish from "./locales/tr.json";
import { translationOverrides } from "./locales/overrides";
import { movingTranslations } from "./locales/moving";
import { networkTranslations } from "./locales/network";
import { articleTranslations } from "./editorial";

export type Locale = "en" | "de" | "tr";
export const LOCALES: Locale[] = ["en", "de", "tr"];
export const LOCALE_LABELS = { en: "English", de: "Deutsch", tr: "Türkçe" } as const;
export const DATE_LOCALES = { en: "en-GB", de: "de-DE", tr: "tr-TR" } as const;
export const OG_LOCALES = { en: "en_GB", de: "de_DE", tr: "tr_TR" } as const;
export function localeFromPath(url: string): Locale {
  const first = url.split(/[?#]/)[0].split("/")[1];
  return first === "de" || first === "tr" ? first : "en";
}
export function stripLocale(url: string) {
  return url.replace(/^\/(de|tr|en)(?=\/|\?|#|$)/, "").replace(/^(?=[?#]|$)/, "/");
}
export function localizedPath(url: string, locale: Locale) {
  if (!url.startsWith("/") || url.startsWith("//") || /^\/(api|manus-storage|assets|src)(\/|$)/.test(url)) return url;
  const bare = stripLocale(url);
  if (locale === "en") return bare;
  return `/${locale}${bare === "/" ? "" : bare.startsWith("/?") || bare.startsWith("/#") ? bare.slice(1) : bare}`;
}

const normalize = (s: string) => s.replace(/\s+/g, " ").trim();
const catalogs: Record<"en" | "tr", Record<string, string>> = { en: { ...english, ...translationOverrides.en, ...movingTranslations.en, ...networkTranslations.en, ...articleTranslations.en }, tr: { ...turkish, ...translationOverrides.tr, ...movingTranslations.tr, ...networkTranslations.tr, ...articleTranslations.tr } };
const folded = Object.fromEntries(Object.entries(catalogs).map(([locale, dict]) => [locale, new Map(Object.entries(dict).map(([key,value]) => [normalize(key).toLocaleLowerCase("de"), value]))])) as Record<"en" | "tr", Map<string,string>>;
const escapePattern = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const templates = Object.keys(english).filter(key => /\{\d+\}/.test(key) && key.replace(/\{\d+\}/g, "").trim().length > 4).map(key => ({
  key,
  ids: Array.from(key.matchAll(/\{(\d+)\}/g)).map(match => match[1]),
  pattern: new RegExp("^" + key.split(/(\{\d+\})/).map(part => /^\{\d+\}$/.test(part) ? "(.+?)" : escapePattern(part)).join("") + "$"),
}));

export function translateText(text: string, locale: Locale, depth = 0): string {
  if (locale === "de" || !text.trim()) return text;
  const key = normalize(text);
  const dict = catalogs[locale];
  let result = dict[key];
  if (!result) {
    const value = folded[locale].get(key.toLocaleLowerCase("de"));
    if (value) result = key === key.toLocaleUpperCase("de") ? value.toLocaleUpperCase(locale) : value;
  }
  if (!result && depth < 2) {
    for (const template of templates) {
      const match = key.match(template.pattern);
      if (match) {
        result = dict[template.key];
        template.ids.forEach((id,index) => { result = result.replace(`{${id}}`, translateText(match[index+1], locale, depth+1)); });
        break;
      }
    }
  }
  if (!result && key.includes(" | ")) result = key.split(" | ").map(part => translateText(part, locale, depth+1)).join(" | ");
  if (!result) return text;
  return text.slice(0, text.length - text.trimStart().length) + result + text.slice(text.trimEnd().length);
}

/** Translate only presentation values, never mutate original IDs, query inputs or user data. */
export function translateValue<T>(value: T, locale: Locale): T {
  if (typeof value === "string") return translateText(value, locale) as T;
  if (Array.isArray(value)) return value.map(item => translateValue(item, locale)) as T;
  return value;
}
export function translateTemplate(key: string, values: unknown[], locale: Locale) {
  return translateText(key, locale).replace(/\{(\d+)\}/g, (_,index) => String(translateValue(values[Number(index)], locale) ?? ""));
}
